import React, { useState, useEffect, useMemo } from 'react';
import { getToken } from '../services/authService';
import { ImageViewer } from '../components/common/ImageViewer';

const API = process.env.REACT_APP_BACKEND_URL;

const APPROVAL_CHECKLIST = [
  { id: 'name', label: 'Valid dhaba name & address' },
  { id: 'location', label: 'Location coordinates are accurate' },
  { id: 'owner', label: 'Owner details complete (name, phone)' },
  { id: 'cuisine', label: 'At least one cuisine type selected' },
  { id: 'highway', label: 'Highway information provided' },
  { id: 'photos', label: 'Photos uploaded (optional for MVP)' },
];

const HYGIENE_SECTIONS = [
  {
    id: 'kitchen_hygiene', label: 'Kitchen Hygiene', maxScore: 1.40,
    items: [
      { id: 'clean_cooking_surfaces', label: 'Clean cooking surfaces', weight: 0.28 },
      { id: 'no_pests_kitchen', label: 'No pests (flies, rodents)', weight: 0.28 },
      { id: 'waste_disposal', label: 'Proper waste disposal', weight: 0.20 },
      { id: 'covered_food_storage', label: 'Covered food storage', weight: 0.28 },
      { id: 'clean_utensils', label: 'Clean utensils', weight: 0.20 },
      { id: 'staff_gloves_headcover', label: 'Staff using gloves/head cover', weight: 0.16 },
    ],
  },
  {
    id: 'food_handling', label: 'Food Handling & Safety', maxScore: 1.30,
    items: [
      { id: 'raw_cooked_separation', label: 'Raw & cooked food separation', weight: 0.28 },
      { id: 'fresh_ingredients', label: 'Fresh ingredients', weight: 0.28 },
      { id: 'proper_storage', label: 'Proper storage (fridge if needed)', weight: 0.28 },
      { id: 'no_stale_reuse', label: 'No stale food reuse', weight: 0.28 },
      { id: 'safe_water', label: 'Safe water usage', weight: 0.18 },
    ],
  },
  {
    id: 'dining_area', label: 'Dining Area Cleanliness', maxScore: 0.90,
    items: [
      { id: 'clean_tables', label: 'Clean tables', weight: 0.18 },
      { id: 'no_litter', label: 'No litter', weight: 0.18 },
      { id: 'dust_free_seating', label: 'Dust-free seating', weight: 0.15 },
      { id: 'clean_serving_area', label: 'Clean serving area', weight: 0.15 },
      { id: 'clean_cutlery', label: 'Clean cutlery', weight: 0.14 },
      { id: 'sufficient_tissues', label: 'Sufficient tissue papers', weight: 0.10 },
    ],
  },
  {
    id: 'staff_hygiene', label: 'Staff Hygiene', maxScore: 0.92,
    items: [
      { id: 'clean_uniform', label: 'Clean clothes/uniform', weight: 0.24 },
      { id: 'no_barehand', label: 'No bare-hand food handling', weight: 0.24 },
      { id: 'basic_grooming', label: 'Basic grooming', weight: 0.22 },
      { id: 'polite_behaviour', label: 'Polite behaviour', weight: 0.22 },
    ],
  },
  {
    id: 'general', label: 'General Hygiene & Facilities', maxScore: 0.48,
    items: [
      { id: 'handwash_stations', label: 'Handwash/sanitizer stations available', weight: 0.10 },
      { id: 'pest_control_measures', label: 'Pest control measures in place', weight: 0.10 },
    ],
    hasOverallImpression: true,
    overallImpressionMaxWeight: 0.28,
  },
];

const OVERALL_IMPRESSION_OPTIONS = [
  { value: 'poor', label: 'Poor', score: 0.00 },
  { value: 'average', label: 'Average', score: 0.09 },
  { value: 'good', label: 'Good', score: 0.19 },
  { value: 'excellent', label: 'Excellent', score: 0.28 },
];

const ALL_HYGIENE_ITEMS = HYGIENE_SECTIONS.flatMap(s => s.items);

const WASHROOM_SECTIONS = [
  {
    id: 'cleanliness', label: 'Cleanliness', maxScore: 1.20,
    items: [
      { id: 'toilet_seat_clean', label: 'Toilet seat clean (no stains)', weight: 0.28 },
      { id: 'floor_clean_dry', label: 'Floor clean and dry', weight: 0.25 },
      { id: 'no_foul_smell', label: 'No foul smell', weight: 0.25 },
      { id: 'walls_tiles_clean', label: 'Walls and tiles clean', weight: 0.22 },
      { id: 'dustbin_present', label: 'Dustbin present and not overflowing', weight: 0.20 },
    ],
  },
  {
    id: 'water_plumbing', label: 'Water & Plumbing', maxScore: 1.10,
    items: [
      { id: 'running_water', label: 'Running water available', weight: 0.25 },
      { id: 'flush_working', label: 'Flush working properly', weight: 0.25 },
      { id: 'no_leakage', label: 'No leakage or overflow', weight: 0.20 },
      { id: 'sink_functional', label: 'Sink functional', weight: 0.20 },
      { id: 'drainage_working', label: 'Drainage working', weight: 0.20 },
    ],
  },
  {
    id: 'essentials', label: 'Essentials Availability', maxScore: 0.76,
    items: [
      { id: 'soap_handwash', label: 'Soap or handwash available', weight: 0.22 },
      { id: 'hand_drying', label: 'Hand drying (tissue/air)', weight: 0.18 },
      { id: 'toilet_paper', label: 'Toilet paper', weight: 0.18 },
      { id: 'mirror_available', label: 'Mirror', weight: 0.18 },
    ],
  },
  {
    id: 'safety_usability', label: 'Safety & Usability', maxScore: 0.94,
    items: [
      { id: 'proper_lighting', label: 'Proper lighting', weight: 0.20 },
      { id: 'door_lock_working', label: 'Door lock working', weight: 0.20 },
      { id: 'privacy_maintained', label: 'Privacy maintained', weight: 0.18 },
      { id: 'no_broken_fixtures', label: 'No broken fixtures', weight: 0.18 },
      { id: 'feels_safe_women', label: 'Feels safe to use (especially for women)', weight: 0.18 },
    ],
  },
  {
    id: 'accessibility_comfort', label: 'Accessibility & Comfort', maxScore: 1.00,
    items: [
      { id: 'separate_women_washroom', label: 'Separate washroom for women', weight: 0.24 },
      { id: 'clear_signage', label: 'Clear signage', weight: 0.14 },
      { id: 'easy_access_parking', label: 'Easy access from parking', weight: 0.14 },
      { id: 'adequate_space', label: 'Adequate space', weight: 0.12 },
      { id: 'queue_management', label: 'Queue management', weight: 0.08 },
    ],
    hasOverallImpression: true,
    overallImpressionMaxWeight: 0.28,
  },
];

const WASHROOM_IMPRESSION_OPTIONS = [
  { value: 'poor', label: 'Poor', score: 0.00 },
  { value: 'average', label: 'Average', score: 0.09 },
  { value: 'good', label: 'Good', score: 0.19 },
  { value: 'excellent', label: 'Excellent', score: 0.28 },
];

const ALL_WASHROOM_ITEMS = WASHROOM_SECTIONS.flatMap(s => s.items);

function calcScore(checks, criteria) {
  return criteria.reduce((sum, c) => sum + (checks[c.id] ? c.weight : 0), 0);
}

function scoreColor(score, max) {
  const pct = score / max;
  if (pct >= 0.8) return '#22C55E';
  if (pct >= 0.6) return '#F59E0B';
  return '#EF4444';
}

function fetchAdmin(path) {
  return fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json());
}

export function AdminPanel({ onBack, user }) {
  const [tab, setTab] = useState('pending');
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('list');

  const [admins, setAdmins] = useState([]);
  const [newAdminPhone, setNewAdminPhone] = useState('');
  const [newAdminName, setNewAdminName] = useState('');

  // Review state
  const [approvalChecks, setApprovalChecks] = useState({});
  const [hygieneChecks, setHygieneChecks] = useState({});
  const [washroomChecks, setWashroomChecks] = useState({});
  const [overallImpression, setOverallImpression] = useState('');
  const [washroomImpression, setWashroomImpression] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [infoRequestRemarks, setInfoRequestRemarks] = useState('');
  const [activeAction, setActiveAction] = useState(null); // 'approve' | 'reject' | 'request_info'
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [auditHistory, setAuditHistory] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [showHygieneBreakdown, setShowHygieneBreakdown] = useState(false);
  const [showWashroomBreakdown, setShowWashroomBreakdown] = useState(false);
  const [viewerImages, setViewerImages] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  // Snapshot of audit state as initially loaded (pre-populated from latest audit or empty).
  // Used to detect whether admin has actually modified the audit → decides whether to submit a new audit.
  const initialAuditSnapshotRef = React.useRef('');
  const auditModified = React.useMemo(() => {
    const current = JSON.stringify({
      h: hygieneChecks, w: washroomChecks, hi: overallImpression, wi: washroomImpression,
    });
    return initialAuditSnapshotRef.current !== '' && current !== initialAuditSnapshotRef.current;
  }, [hygieneChecks, washroomChecks, overallImpression, washroomImpression]);

  const impressionScore = useMemo(() => {
    const opt = OVERALL_IMPRESSION_OPTIONS.find(o => o.value === overallImpression);
    return opt ? opt.score : 0;
  }, [overallImpression]);
  const washroomImpressionScore = useMemo(() => {
    const opt = WASHROOM_IMPRESSION_OPTIONS.find(o => o.value === washroomImpression);
    return opt ? opt.score : 0;
  }, [washroomImpression]);
  const hygieneScore = useMemo(() => calcScore(hygieneChecks, ALL_HYGIENE_ITEMS) + impressionScore, [hygieneChecks, impressionScore]);
  const washroomRating = useMemo(() => calcScore(washroomChecks, ALL_WASHROOM_ITEMS) + washroomImpressionScore, [washroomChecks, washroomImpressionScore]);
  const allApprovalChecked = APPROVAL_CHECKLIST.filter(c => c.id !== 'photos').every(c => approvalChecks[c.id]);

  useEffect(() => { loadRegistrations(); }, [tab]);

  const loadRegistrations = async () => {
    setLoading(true);
    try {
      const data = await fetchAdmin(`/api/admin/registrations/${tab}`);
      setRegistrations(data.registrations || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  const loadAdmins = async () => {
    try { const data = await fetchAdmin('/api/admin/dhaba-admins'); setAdmins(data.admins || []); } catch (e) { /* silent */ }
  };

  const loadAuditHistory = async (dhabaId) => {
    try { const data = await fetchAdmin(`/api/admin/registrations/${dhabaId}/audits`); setAuditHistory(data.audits || []); } catch (e) { /* silent */ }
    try { const data = await fetchAdmin(`/api/admin/registrations/${dhabaId}/activity`); setActivityLog(data.activities || []); } catch (e) { /* silent */ }
  };

  const openReview = (dhaba) => {
    setSelected(dhaba);
    setView('review');
    setApprovalChecks({});
    setHygieneChecks({});
    setWashroomChecks({});
    setOverallImpression('');
    setWashroomImpression('');
    setAdminNotes('');
    setRejectionReason('');
    setInfoRequestRemarks('');
    setActiveAction(null);
    setMessage('');
    setAuditHistory([]);
    // Reset snapshot — will be populated after pre-load below
    initialAuditSnapshotRef.current = '';
    setActivityLog([]);
    setShowHygieneBreakdown(false);
    setShowWashroomBreakdown(false);

    // Auto-check approval criteria
    const auto = {};
    if (dhaba.name && dhaba.address) auto.name = true;
    if (dhaba.latitude && dhaba.longitude) auto.location = true;
    if (dhaba.owner_name && dhaba.owner_phone) auto.owner = true;
    if (dhaba.cuisine?.length > 0) auto.cuisine = true;
    if (dhaba.highway) auto.highway = true;
    if (dhaba.images?.length > 0) auto.photos = true;
    setApprovalChecks(auto);

    loadAuditHistory(dhaba.id);

    // Lazy-load photos + full pending_changes (with URLs) from detail endpoint
    fetchAdmin(`/api/admin/registrations/${dhaba.id}`).then(data => {
      if (data) {
        setSelected(prev => prev?.id === dhaba.id ? {
          ...prev,
          photos: data.photos ?? prev.photos,
          images: data.images ?? prev.images,
          // detail endpoint returns full pending_changes with photo URLs (list endpoint strips them)
          pending_changes: data.pending_changes ?? prev.pending_changes,
        } : prev);
        const hasPhotos = data.photos && Object.values(data.photos).some(arr => arr?.length > 0);
        if (hasPhotos) setApprovalChecks(prev => ({ ...prev, photos: true }));
      }
    });

    // Load saved draft (partial audit progress) — falls back to latest audit if no draft
    fetchAdmin(`/api/admin/registrations/${dhaba.id}/audit-draft`).then(async data => {
      let restoredFromDraft = false;
      let hSnap = {}, wSnap = {}, hImpSnap = '', wImpSnap = '';
      if (data?.draft) {
        const d = data.draft;
        if (d.hygiene_checks && Object.keys(d.hygiene_checks).length > 0) {
          const { overall_impression, ...hChecks } = d.hygiene_checks;
          setHygieneChecks(hChecks); hSnap = hChecks;
          if (overall_impression) { setOverallImpression(overall_impression); hImpSnap = overall_impression; }
          restoredFromDraft = true;
        }
        if (d.washroom_checks && Object.keys(d.washroom_checks).length > 0) {
          const { overall_impression: wImpression, ...wChecks } = d.washroom_checks;
          setWashroomChecks(wChecks); wSnap = wChecks;
          if (wImpression) { setWashroomImpression(wImpression); wImpSnap = wImpression; }
          restoredFromDraft = true;
        }
        if (d.admin_notes) setAdminNotes(d.admin_notes);
        if (restoredFromDraft) setMessage(`Draft restored from ${new Date(d.updated_at).toLocaleString()}`);
      }
      if (!restoredFromDraft) {
        // Pre-populate checklist from the most recent audit (preserves previous audit state on re-approval)
        try {
          const audits = await fetchAdmin(`/api/admin/registrations/${dhaba.id}/audits`);
          const latest = audits?.audits?.[0];
          if (latest) {
            const hc = typeof latest.hygiene_criteria === 'string' ? JSON.parse(latest.hygiene_criteria) : (latest.hygiene_criteria || {});
            const wc = typeof latest.washroom_criteria === 'string' ? JSON.parse(latest.washroom_criteria) : (latest.washroom_criteria || {});
            const { overall_impression: hImp, ...hChecks } = hc;
            const { overall_impression: wImp, ...wChecks } = wc;
            if (Object.keys(hChecks).length > 0) { setHygieneChecks(hChecks); hSnap = hChecks; }
            if (hImp) { setOverallImpression(hImp); hImpSnap = hImp; }
            if (Object.keys(wChecks).length > 0) { setWashroomChecks(wChecks); wSnap = wChecks; }
            if (wImp) { setWashroomImpression(wImp); wImpSnap = wImp; }
            const auditDate = latest.audit_date ? new Date(latest.audit_date).toLocaleDateString() : 'earlier';
            setMessage(`Previous audit values restored (from ${auditDate}). Re-audit only if changes warrant it.`);
          }
        } catch { /* silent */ }
      }
      // Store initial snapshot so we can detect if admin modifies the audit later
      initialAuditSnapshotRef.current = JSON.stringify({ h: hSnap, w: wSnap, hi: hImpSnap, wi: wImpSnap });
    });
  };

  const saveDraft = async () => {
    if (!selected) return;
    try {
      await fetch(`${API}/api/admin/registrations/${selected.id}/audit-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          hygiene_checks: { ...hygieneChecks, overall_impression: overallImpression },
          washroom_checks: { ...washroomChecks, overall_impression: washroomImpression },
          admin_notes: adminNotes || null,
        }),
      });
      setMessage('Draft saved');
      setTimeout(() => setMessage(''), 2000);
    } catch (e) {
      setMessage('Failed to save draft');
    }
  };

  const deleteDraft = async (dhabaId) => {
    try {
      await fetch(`${API}/api/admin/registrations/${dhabaId}/audit-draft`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` },
      });
    } catch (e) { /* silent */ }
  };

  const handleReview = async (action) => {
    if (action === 'reject' && !rejectionReason.trim()) { setMessage('Please provide a rejection reason'); return; }
    if (action === 'request_info' && !infoRequestRemarks.trim()) { setMessage('Please provide remarks for the owner'); return; }
    setSubmitting(true);
    setMessage('');
    try {
      // Only submit a new audit if the admin has MODIFIED the audit state since opening the dhaba.
      // (If the checklists were pre-populated from a prior audit but admin didn't change anything,
      //  preserve the previous audit record — don't create a duplicate.)
      const shouldSubmitNewAudit = auditModified && (Object.keys(hygieneChecks).length > 0 || Object.keys(washroomChecks).length > 0);
      if (shouldSubmitNewAudit) {
        const auditPayload = { hygiene_checks: { ...hygieneChecks, overall_impression: overallImpression }, washroom_checks: { ...washroomChecks, overall_impression: washroomImpression }, notes: adminNotes || null };
        try {
          const auditRes = await fetch(`${API}/api/admin/registrations/${selected.id}/audit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify(auditPayload),
          });
          if (!auditRes.ok) {
            const errData = await auditRes.json().catch(() => ({}));
            setMessage(`Audit failed: ${errData.detail || auditRes.status}. Action aborted.`);
            setSubmitting(false);
            return;
          }
        } catch (auditErr) {
          setMessage(`Audit submission failed: ${auditErr.message}. Action aborted.`);
          setSubmitting(false);
          return;
        }
      }
      const res = await fetch(`${API}/api/admin/registrations/${selected.id}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          action,
          // Only send new scores if admin actually modified the audit this session.
          // Otherwise backend COALESCE preserves existing values.
          hygiene_score: shouldSubmitNewAudit ? hygieneScore : null,
          washroom_rating: shouldSubmitNewAudit ? washroomRating : null,
          is_verified: true, admin_notes: adminNotes || null, rejection_reason: rejectionReason || null,
          info_request_remarks: infoRequestRemarks || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const msgs = { approve: 'Dhaba approved!', reject: 'Dhaba rejected', request_info: 'Info requested from owner' };
        setMessage(msgs[action] || 'Done');
        setTimeout(() => { deleteDraft(selected.id); setView('list'); loadRegistrations(); }, 1000);
      } else { setMessage(data.detail || 'Action failed'); }
    } catch (e) { setMessage('Action failed'); }
    setSubmitting(false);
  };

  const handleCreateAdmin = async () => {
    if (!newAdminPhone.trim()) return;
    try {
      await fetch(`${API}/api/admin/create-dhaba-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ phone: newAdminPhone, name: newAdminName || null }),
      });
      setNewAdminPhone(''); setNewAdminName(''); loadAdmins();
    } catch (e) { /* silent */ }
  };

  // ---- Manage Admins ----
  if (view === 'admins') {
    return (
      <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>
        <Header title="Manage Dhaba Admins" onBack={() => setView('list')} />
        <div style={{ padding: '16px' }}>
          <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px' }}>Create Dhaba Admin</h3>
            <input value={newAdminPhone} onChange={e => setNewAdminPhone(e.target.value)} placeholder="Phone number" data-testid="admin-phone-input"
              style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '10px', marginBottom: '8px', fontSize: '14px', outline: 'none' }} />
            <input value={newAdminName} onChange={e => setNewAdminName(e.target.value)} placeholder="Name (optional)" data-testid="admin-name-input"
              style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '10px', marginBottom: '12px', fontSize: '14px', outline: 'none' }} />
            <button onClick={handleCreateAdmin} data-testid="create-admin-btn"
              style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '15px', cursor: 'pointer' }}>
              Create Dhaba Admin
            </button>
          </div>
          {admins.map(a => (
            <div key={a.id} style={{ background: '#FFF', borderRadius: '12px', padding: '14px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div><p style={{ fontWeight: '600', color: '#1A1A1A', fontSize: '14px' }}>{a.name || 'Unnamed'}</p><p style={{ fontSize: '12px', color: '#6B7280' }}>{a.phone} · {a.role}</p></div>
              {a.role === 'dhaba_admin' && (
                <button onClick={async () => { await fetch(`${API}/api/admin/dhaba-admin/${a.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } }); loadAdmins(); }}
                  style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #EF4444', background: 'transparent', color: '#EF4444', fontSize: '12px', cursor: 'pointer' }}>Remove</button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---- Settlements View ----
  if (view === 'settlements') {
    return <SettlementsView onBack={() => setView('list')} />;
  }

  // ---- Review View ----
  if (view === 'review' && selected) {
    return (
      <div style={{ minHeight: '100vh', background: '#F9FAFB', paddingBottom: '100px' }}>
        <Header title="Review Registration" onBack={() => setView('list')} />
        <div style={{ padding: '16px' }}>
          {/* Dhaba Summary */}
          <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1A1A1A' }}>{selected.name}</h2>
              <StatusBadge status={selected.registration_status} />
            </div>
            {selected.display_id && (
              <p style={{ fontSize: '13px', color: '#D4AF37', fontWeight: '600', marginBottom: '12px', fontFamily: 'monospace' }}>{selected.display_id}</p>
            )}
            {/* Certification badges */}
            {(selected.ht_certified_dhaba || selected.ht_certified_dining || selected.ht_certified_washroom) && (
              <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                {selected.ht_certified_dhaba && <CertBadge type="dhaba" />}
                {selected.ht_certified_dining && !selected.ht_certified_dhaba && <CertBadge type="dining" />}
                {selected.ht_certified_washroom && !selected.ht_certified_dhaba && <CertBadge type="washroom" />}
              </div>
            )}
            <InfoRow label="Type" value={({ dhaba: 'Dhaba', restaurant: 'Restaurant', food_court: 'Food Court' })[selected.dhaba_type] || selected.dhaba_type} />
            <InfoRow label="Description" value={selected.description} />
            <InfoRow label="Highway" value={`${selected.highway}${selected.highway_km ? ` (KM ${selected.highway_km})` : ''}`} />
            <InfoRow label="Address" value={selected.address} />
            <InfoRow label="Location" value={selected.latitude && selected.longitude ? `${selected.latitude}, ${selected.longitude}` : 'Not provided'} />
            <InfoRow label="Cuisine" value={(selected.cuisine || []).join(', ') || 'None'} />
            <InfoRow label="Price Range" value={selected.price_range} />
            <InfoRow label="Veg/Non-Veg" value={(selected.tags || []).includes('Pure Veg') ? 'Pure Veg' : 'Non-Veg / Both'} />
            <InfoRow label="Timings" value={selected.open_24x7 ? '24x7' : `${selected.open_time || '?'} - ${selected.close_time || '?'}`} />
            <InfoRow label="Contact Phone" value={selected.phone} />
            <InfoRow label="Tags" value={(selected.tags || []).join(', ')} />
            <InfoRow label="Registered on" value={selected.created_at ? new Date(selected.created_at).toLocaleString() : '—'} />
            {/* Scores — tappable individually */}
            {(selected.hygiene_score > 0 || selected.washroom_rating > 0 || auditHistory.length > 0) && (
              <div style={{ marginTop: '8px', borderTop: '1px solid #F3F4F6' }}>
                {/* Hygiene Score */}
                {selected.hygiene_score > 0 && (
                  <div>
                    <div onClick={() => setShowHygieneBreakdown(!showHygieneBreakdown)}
                      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
                      <span style={{ fontSize: '13px' }}>
                        <span style={{ color: '#6B7280' }}>Hygiene Score: </span>
                        <span style={{ fontWeight: '700', color: scoreColor(selected.hygiene_score || 0, 5) }}>{parseFloat(selected.hygiene_score || 0).toFixed(1)}/5</span>
                      </span>
                      <span style={{ fontSize: '12px', color: '#D4AF37', fontWeight: '600' }}>
                        {showHygieneBreakdown ? 'Hide ▲' : 'Details ▼'}
                      </span>
                    </div>
                    {showHygieneBreakdown && auditHistory.length > 0 && (() => {
                      const latest = auditHistory[0];
                      const hc = latest.hygiene_criteria || {};
                      return (
                        <div style={{ padding: '12px', background: '#F9FAFB', borderRadius: '12px', marginTop: '4px', marginBottom: '8px' }}>
                          <p style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '10px' }}>
                            Last audit: {new Date(latest.audit_date).toLocaleDateString()} by {latest.auditor_name}
                          </p>
                          {HYGIENE_SECTIONS.map(section => {
                            const sectionScore = section.items.reduce((sum, c) => sum + (hc[c.id] ? c.weight : 0), 0);
                            return (
                              <div key={section.id} style={{ marginBottom: '10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{section.label}</span>
                                  <span style={{ fontSize: '12px', fontWeight: '700', color: scoreColor(sectionScore, section.maxScore) }}>
                                    {sectionScore.toFixed(2)}/{section.maxScore.toFixed(2)}
                                  </span>
                                </div>
                                {section.items.map(c => (
                                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0' }}>
                                    <span style={{ fontSize: '11px', color: hc[c.id] ? '#22C55E' : '#D1D5DB' }}>{hc[c.id] ? '✓' : '✕'}</span>
                                    <span style={{ fontSize: '11px', color: hc[c.id] ? '#374151' : '#9CA3AF' }}>{c.label}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                          {hc.overall_impression && (
                            <div style={{ padding: '4px 0' }}>
                              <span style={{ fontSize: '12px', color: '#6B7280' }}>Overall Impression: </span>
                              <span style={{ fontSize: '12px', fontWeight: '600', color: '#1A1A1A', textTransform: 'capitalize' }}>{hc.overall_impression}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {showHygieneBreakdown && auditHistory.length === 0 && (
                      <p style={{ fontSize: '12px', color: '#9CA3AF', padding: '8px 0' }}>No audit records found</p>
                    )}
                  </div>
                )}

                {/* Washroom Score */}
                {selected.washroom_rating > 0 && (
                  <div>
                    <div onClick={() => setShowWashroomBreakdown(!showWashroomBreakdown)}
                      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                      <span style={{ fontSize: '13px' }}>
                        <span style={{ color: '#6B7280' }}>Washroom Rating: </span>
                        <span style={{ fontWeight: '700', color: scoreColor(selected.washroom_rating || 0, 5) }}>{parseFloat(selected.washroom_rating || 0).toFixed(1)}/5</span>
                      </span>
                      <span style={{ fontSize: '12px', color: '#D4AF37', fontWeight: '600' }}>
                        {showWashroomBreakdown ? 'Hide ▲' : 'Details ▼'}
                      </span>
                    </div>
                    {showWashroomBreakdown && auditHistory.length > 0 && (() => {
                      const latest = auditHistory[0];
                      const wc = latest.washroom_criteria || {};
                      return (
                        <div style={{ padding: '12px', background: '#F9FAFB', borderRadius: '12px', marginTop: '4px' }}>
                          <p style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '10px' }}>
                            Last audit: {new Date(latest.audit_date).toLocaleDateString()} by {latest.auditor_name}
                          </p>
                          {WASHROOM_SECTIONS.map(section => {
                            const sectionScore = section.items.reduce((sum, c) => sum + (wc[c.id] ? c.weight : 0), 0);
                            return (
                              <div key={section.id} style={{ marginBottom: '10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{section.label}</span>
                                  <span style={{ fontSize: '12px', fontWeight: '700', color: scoreColor(sectionScore, section.maxScore) }}>
                                    {sectionScore.toFixed(2)}/{section.maxScore.toFixed(2)}
                                  </span>
                                </div>
                                {section.items.map(c => (
                                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0' }}>
                                    <span style={{ fontSize: '11px', color: wc[c.id] ? '#22C55E' : '#D1D5DB' }}>{wc[c.id] ? '✓' : '✕'}</span>
                                    <span style={{ fontSize: '11px', color: wc[c.id] ? '#374151' : '#9CA3AF' }}>{c.label}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                    {showWashroomBreakdown && auditHistory.length === 0 && (
                      <p style={{ fontSize: '12px', color: '#9CA3AF', padding: '8px 0' }}>No audit records found</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Photos — Categorized */}
          {(selected.photos && Object.values(selected.photos).some(arr => arr?.length > 0)) ? (
            <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px' }}>Photos</h3>
              {[
                { id: 'exterior', label: 'Exterior / Entrance' },
                { id: 'parking', label: 'Parking Area' },
                { id: 'dining', label: 'Dining Area' },
                { id: 'kitchen', label: 'Kitchen / Cooking Area' },
                { id: 'food_storage', label: 'Food Display / Storage' },
                { id: 'menu_board', label: 'Menu Board' },
                { id: 'washroom', label: 'Washroom' },
              ].map(cat => {
                const imgs = (selected.photos?.[cat.id]) || [];
                if (imgs.length === 0) return null;
                return (
                  <div key={cat.id} style={{ marginBottom: '12px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', marginBottom: '6px' }}>{cat.label} ({imgs.length})</p>
                    <div style={{ display: 'flex', gap: '6px', overflowX: 'auto' }}>
                      {imgs.map((img, idx) => (
                        <img key={`${cat.id}-${idx}`} src={img} alt={cat.label}
                          onClick={() => { setViewerImages(imgs); setViewerIndex(idx); }}
                          style={{ width: '90px', height: '68px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E5E7EB', cursor: 'pointer' }} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : selected.images?.length > 0 ? (
            <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px' }}>Photos ({selected.images.length})</h3>
              <div style={{ display: 'flex', gap: '8px', overflowX: 'auto' }}>
                {selected.images.map((img, idx) => (
                  <img key={`img-${idx}`} src={img} alt=""
                    onClick={() => { setViewerImages(selected.images); setViewerIndex(idx); }}
                    style={{ width: '100px', height: '75px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E5E7EB', cursor: 'pointer' }} />
                ))}
              </div>
            </div>
          ) : null}

          {/* Pending Changes Diff */}
          {selected.pending_changes && Object.keys(selected.pending_changes).length > 0 && (
            <div style={{ background: '#FFFBEB', borderRadius: '16px', padding: '20px', marginBottom: '16px', border: '2px solid #FDE68A', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#92400E', marginBottom: '12px' }}>Changes Requiring Review</h3>

              {/* Changes Delta banner + one-click approve shortcut */}
              {(() => {
                const fieldLabels = {
                  price_range: 'Price Range', cuisine: 'Cuisine', parking: 'Parking', washroom: 'Washroom',
                  ac_dining: 'AC Dining', wifi: 'WiFi', card_accepted: 'Cards Accepted',
                  family_friendly: 'Family Friendly', play_area: 'Play Area', ev_charging: 'EV Charging',
                  parking_type: 'Parking Type', images: 'Photos', photos: 'Photos', pure_veg: 'Pure Veg',
                  description: 'Description', open_time: 'Opening Time', close_time: 'Closing Time',
                  open_24x7: '24×7 Hours', address: 'Address', tags: 'Tags',
                };
                const changedKeys = Object.keys(selected.pending_changes);
                const deltaSummary = changedKeys.map(k => fieldLabels[k] || k).join(' + ');
                return (
                  <div data-testid="changes-delta-banner" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                    padding: '10px 12px', marginBottom: '14px',
                    background: '#FEF3C7', borderRadius: '10px', border: '1px dashed #D97706',
                    flexWrap: 'wrap',
                  }}>
                    <div style={{ flex: 1, minWidth: '180px' }}>
                      <p style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>
                        Owner Edited
                      </p>
                      <p style={{ fontSize: '13px', fontWeight: '600', color: '#78350F' }}>{deltaSummary}</p>
                      <p style={{ fontSize: '11px', color: '#92400E', marginTop: '2px', opacity: 0.8 }}>
                        Hygiene & Washroom audit remains unchanged
                      </p>
                    </div>
                    <button
                      onClick={() => handleReview('approve')}
                      disabled={submitting}
                      data-testid="quick-approve-changes-btn"
                      style={{
                        padding: '8px 14px', borderRadius: '10px', border: 'none',
                        background: submitting ? '#9CA3AF' : 'linear-gradient(135deg, #10B981, #059669)',
                        color: '#FFF', fontWeight: '700', fontSize: '12px',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {submitting ? 'Approving...' : '✓ Approve Changes'}
                    </button>
                  </div>
                );
              })()}

              {Object.entries(selected.pending_changes).map(([field, change]) => {
                const fieldLabels = {
                  price_range: 'Price Range', cuisine: 'Cuisine', parking: 'Parking', washroom: 'Washroom',
                  ac_dining: 'AC Dining', wifi: 'WiFi', card_accepted: 'Cards Accepted',
                  family_friendly: 'Family Friendly', play_area: 'Play Area', ev_charging: 'EV Charging',
                  parking_type: 'Parking Type', images: 'Photos', photos: 'Photos', pure_veg: 'Pure Veg',
                };
                const label = fieldLabels[field] || field;

                if (field === 'images' || field === 'photos') {
                  // Flatten photos structure (categorized: { category: { added: [], removed: [] } })
                  let added = [];
                  let removed = [];
                  if (change.added || change.removed) {
                    // Flat images diff
                    added = change.added || [];
                    removed = change.removed || [];
                  } else {
                    // Categorized photos diff
                    Object.values(change || {}).forEach(catDiff => {
                      if (catDiff?.added) added = added.concat(catDiff.added);
                      if (catDiff?.removed) removed = removed.concat(catDiff.removed);
                    });
                  }
                  return (
                    <div key={field} style={{ marginBottom: '12px' }}>
                      <p style={{ fontSize: '13px', fontWeight: '600', color: '#92400E', marginBottom: '8px' }}>{label}</p>
                      {added.length === 0 && removed.length === 0 && (
                        <p style={{ fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>No visible changes</p>
                      )}
                      {added.length > 0 && (
                        <div style={{ marginBottom: '6px' }}>
                          <p style={{ fontSize: '11px', color: '#22C55E', marginBottom: '4px' }}>Added ({added.length}):</p>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {added.map((img, i) => (
                              <img key={`add-${i}`} src={img} alt="" style={{ width: '60px', height: '45px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #22C55E' }} />
                            ))}
                          </div>
                        </div>
                      )}
                      {removed.length > 0 && (
                        <div>
                          <p style={{ fontSize: '11px', color: '#EF4444', marginBottom: '4px' }}>Removed ({removed.length}):</p>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {removed.map((img, i) => (
                              <img key={`rm-${i}`} src={img} alt="" style={{ width: '60px', height: '45px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #EF4444', opacity: 0.5 }} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                if (field === 'cuisine') {
                  return (
                    <div key={field} style={{ marginBottom: '12px' }}>
                      <p style={{ fontSize: '13px', fontWeight: '600', color: '#92400E', marginBottom: '6px' }}>{label}</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {(change.added || []).map(c => (
                          <span key={`a-${c}`} style={{ padding: '4px 10px', borderRadius: '12px', background: '#D1FAE5', color: '#065F46', fontSize: '12px', fontWeight: '600' }}>+ {c}</span>
                        ))}
                        {(change.removed || []).map(c => (
                          <span key={`r-${c}`} style={{ padding: '4px 10px', borderRadius: '12px', background: '#FEE2E2', color: '#991B1B', fontSize: '12px', fontWeight: '600', textDecoration: 'line-through' }}>- {c}</span>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Boolean or simple value change
                const oldDisplay = typeof change.old === 'boolean' ? (change.old ? 'Yes' : 'No') : String(change.old ?? '—');
                const newDisplay = typeof change.new === 'boolean' ? (change.new ? 'Yes' : 'No') : String(change.new ?? '—');
                return (
                  <div key={field} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(253,230,138,0.5)' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#92400E' }}>{label}</span>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: '12px', color: '#EF4444', textDecoration: 'line-through' }}>{oldDisplay}</span>
                      <span style={{ fontSize: '12px', color: '#6B7280', margin: '0 6px' }}>→</span>
                      <span style={{ fontSize: '12px', color: '#22C55E', fontWeight: '600' }}>{newDisplay}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Owner Info */}
          <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px' }}>Owner Details</h3>
            <InfoRow label="Name" value={selected.owner_name} />
            <InfoRow label="Phone" value={selected.owner_phone} />
            <InfoRow label="Email" value={selected.owner_email} />
            <InfoRow label="Registered Address" value={selected.owner_registered_address} />
            <InfoRow label="GSTN" value={selected.owner_gstn} />
          </div>

          {/* Amenities */}
          <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px' }}>Amenities</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {[
                ['Parking', selected.parking],
                ['Washroom', selected.washroom],
                ['AC Dining', selected.ac_dining],
                ['WiFi', selected.wifi],
                ['Cards Accepted', selected.card_accepted],
                ['Family Friendly', selected.family_friendly],
                ['Kids Play Area', selected.play_area],
                ['EV Charging', selected.ev_charging],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 10px', borderRadius: '8px',
                  background: val ? 'rgba(34,197,94,0.1)' : '#F9FAFB', border: val ? '1px solid #22C55E' : '1px solid #E5E7EB' }}>
                  <span style={{ color: val ? '#22C55E' : '#9CA3AF', fontSize: '14px' }}>{val ? '✓' : '✕'}</span>
                  <span style={{ fontSize: '13px', color: val ? '#065F46' : '#9CA3AF' }}>{label}</span>
                </div>
              ))}
            </div>
            {selected.parking && selected.parking_type && (
              <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>Parking type: {selected.parking_type}</p>
            )}
          </div>

          {/* Only show edit sections for pending registrations */}
          {selected.registration_status === 'pending' && (
            <>
              {/* Approval Checklist */}
              <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px' }}>Approval Checklist</h3>
                {APPROVAL_CHECKLIST.map(item => (
                  <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!approvalChecks[item.id]} onChange={() => setApprovalChecks(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                      style={{ width: '20px', height: '20px', accentColor: '#D4AF37' }} />
                    <span style={{ fontSize: '14px', color: approvalChecks[item.id] ? '#1A1A1A' : '#6B7280' }}>{item.label}</span>
                  </label>
                ))}
              </div>

              {/* Hygiene Score — Sectioned Checklist */}
              <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A' }}>Hygiene Assessment</h3>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '24px', fontWeight: '800', color: scoreColor(hygieneScore, 5) }}>{hygieneScore.toFixed(1)}</span>
                    <span style={{ fontSize: '14px', color: '#9CA3AF' }}> / 5</span>
                  </div>
                </div>
                {/* Master Select All */}
                <button onClick={() => {
                  const allIds = ALL_HYGIENE_ITEMS.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
                  const allChecked = ALL_HYGIENE_ITEMS.every(c => hygieneChecks[c.id]);
                  setHygieneChecks(allChecked ? {} : allIds);
                }} style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #D4AF37', background: 'rgba(212,175,55,0.08)', color: '#92700C', fontSize: '12px', fontWeight: '600', cursor: 'pointer', marginBottom: '12px' }}>
                  {ALL_HYGIENE_ITEMS.every(c => hygieneChecks[c.id]) ? 'Deselect All Sections' : 'Select All Sections'}
                </button>
                <div style={{ height: '6px', background: '#E5E7EB', borderRadius: '3px', marginBottom: '20px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(hygieneScore / 5) * 100}%`, background: scoreColor(hygieneScore, 5), borderRadius: '3px', transition: 'width 0.3s ease' }} />
                </div>

                {HYGIENE_SECTIONS.map(section => {
                  const checkScore = section.items.reduce((sum, c) => sum + (hygieneChecks[c.id] ? c.weight : 0), 0);
                  const sectionScore = section.hasOverallImpression ? checkScore + impressionScore : checkScore;
                  const sectionPct = section.maxScore > 0 ? Math.min(100, (sectionScore / section.maxScore) * 100) : 0;
                  const allSectionChecked = section.items.every(c => hygieneChecks[c.id]);
                  return (
                    <div key={section.id} style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>{section.label}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button onClick={() => {
                            const updates = {};
                            section.items.forEach(c => { updates[c.id] = !allSectionChecked; });
                            setHygieneChecks(prev => ({ ...prev, ...updates }));
                          }} style={{ padding: '3px 8px', borderRadius: '6px', border: '1px solid #E5E7EB', background: allSectionChecked ? '#F3F4F6' : '#FFF', color: '#6B7280', fontSize: '10px', fontWeight: '600', cursor: 'pointer' }}>
                            {allSectionChecked ? 'Deselect' : 'Select All'}
                          </button>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: scoreColor(sectionScore, section.maxScore) }}>
                            {sectionScore.toFixed(2)} / {section.maxScore.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div style={{ height: '4px', background: '#E5E7EB', borderRadius: '2px', marginBottom: '10px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${sectionPct}%`, background: scoreColor(sectionScore, section.maxScore), borderRadius: '2px', transition: 'width 0.3s ease' }} />
                      </div>
                      {section.items.map(c => (
                        <label key={c.id} data-testid={`check-${c.id}`}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                            <input type="checkbox" checked={!!hygieneChecks[c.id]} onChange={() => setHygieneChecks(prev => ({ ...prev, [c.id]: !prev[c.id] }))}
                              style={{ width: '18px', height: '18px', accentColor: '#D4AF37' }} />
                            <span style={{ fontSize: '13px', color: hygieneChecks[c.id] ? '#1A1A1A' : '#6B7280' }}>{c.label}</span>
                          </div>
                          <span style={{ fontSize: '11px', color: hygieneChecks[c.id] ? scoreColor(hygieneScore, 5) : '#D1D5DB', fontWeight: '600', minWidth: '40px', textAlign: 'right' }}>
                            +{c.weight.toFixed(2)}
                          </span>
                        </label>
                      ))}
                      {/* Overall Impression selector (only for General section) */}
                      {section.hasOverallImpression && (
                        <div style={{ padding: '12px 0', borderTop: '2px solid #E5E7EB', marginTop: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Overall Impression</span>
                            <span style={{ fontSize: '11px', color: impressionScore > 0 ? scoreColor(hygieneScore, 5) : '#D1D5DB', fontWeight: '600' }}>
                              +{impressionScore.toFixed(2)} / {section.overallImpressionMaxWeight.toFixed(2)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {OVERALL_IMPRESSION_OPTIONS.map(opt => (
                              <button key={opt.value} onClick={() => setOverallImpression(opt.value)}
                                data-testid={`impression-${opt.value}`}
                                style={{
                                  flex: 1, padding: '10px 6px', borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                                  fontSize: '12px', fontWeight: '600', transition: 'all 0.2s ease',
                                  border: overallImpression === opt.value ? '2px solid #D4AF37' : '2px solid #E5E7EB',
                                  background: overallImpression === opt.value ? 'rgba(212,175,55,0.1)' : '#FFF',
                                  color: overallImpression === opt.value ? '#92700C' : '#6B7280',
                                }}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Washroom Rating — Sectioned Checklist */}
              <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A' }}>Washroom Assessment</h3>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '24px', fontWeight: '800', color: scoreColor(washroomRating, 5) }}>{washroomRating.toFixed(1)}</span>
                    <span style={{ fontSize: '14px', color: '#9CA3AF' }}> / 5</span>
                  </div>
                </div>
                {/* Master Select All */}
                <button onClick={() => {
                  const allIds = ALL_WASHROOM_ITEMS.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
                  const allChecked = ALL_WASHROOM_ITEMS.every(c => washroomChecks[c.id]);
                  setWashroomChecks(allChecked ? {} : allIds);
                }} style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #D4AF37', background: 'rgba(212,175,55,0.08)', color: '#92700C', fontSize: '12px', fontWeight: '600', cursor: 'pointer', marginBottom: '12px' }}>
                  {ALL_WASHROOM_ITEMS.every(c => washroomChecks[c.id]) ? 'Deselect All Sections' : 'Select All Sections'}
                </button>
                <div style={{ height: '6px', background: '#E5E7EB', borderRadius: '3px', marginBottom: '20px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(washroomRating / 5) * 100}%`, background: scoreColor(washroomRating, 5), borderRadius: '3px', transition: 'width 0.3s ease' }} />
                </div>

                {WASHROOM_SECTIONS.map(section => {
                  const checkScore = section.items.reduce((sum, c) => sum + (washroomChecks[c.id] ? c.weight : 0), 0);
                  const sectionScore = section.hasOverallImpression ? checkScore + washroomImpressionScore : checkScore;
                  const sectionPct = section.maxScore > 0 ? Math.min(100, (sectionScore / section.maxScore) * 100) : 0;
                  const allSectionChecked = section.items.every(c => washroomChecks[c.id]);
                  return (
                    <div key={section.id} style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>{section.label}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button onClick={() => {
                            const updates = {};
                            section.items.forEach(c => { updates[c.id] = !allSectionChecked; });
                            setWashroomChecks(prev => ({ ...prev, ...updates }));
                          }} style={{ padding: '3px 8px', borderRadius: '6px', border: '1px solid #E5E7EB', background: allSectionChecked ? '#F3F4F6' : '#FFF', color: '#6B7280', fontSize: '10px', fontWeight: '600', cursor: 'pointer' }}>
                            {allSectionChecked ? 'Deselect' : 'Select All'}
                          </button>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: scoreColor(sectionScore, section.maxScore) }}>
                            {sectionScore.toFixed(2)} / {section.maxScore.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div style={{ height: '4px', background: '#E5E7EB', borderRadius: '2px', marginBottom: '10px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${sectionPct}%`, background: scoreColor(sectionScore, section.maxScore), borderRadius: '2px', transition: 'width 0.3s ease' }} />
                      </div>
                      {section.items.map(c => (
                        <label key={c.id} data-testid={`wcheck-${c.id}`}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                            <input type="checkbox" checked={!!washroomChecks[c.id]} onChange={() => setWashroomChecks(prev => ({ ...prev, [c.id]: !prev[c.id] }))}
                              style={{ width: '18px', height: '18px', accentColor: '#D4AF37' }} />
                            <span style={{ fontSize: '13px', color: washroomChecks[c.id] ? '#1A1A1A' : '#6B7280' }}>{c.label}</span>
                          </div>
                          <span style={{ fontSize: '11px', color: washroomChecks[c.id] ? scoreColor(washroomRating, 5) : '#D1D5DB', fontWeight: '600', minWidth: '40px', textAlign: 'right' }}>
                            +{c.weight.toFixed(2)}
                          </span>
                        </label>
                      ))}
                      {/* Washroom Overall Impression */}
                      {section.hasOverallImpression && (
                        <div style={{ padding: '12px 0', borderTop: '2px solid #E5E7EB', marginTop: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Overall Impression</span>
                            <span style={{ fontSize: '11px', color: washroomImpressionScore > 0 ? scoreColor(washroomRating, 5) : '#D1D5DB', fontWeight: '600' }}>
                              +{washroomImpressionScore.toFixed(2)} / {section.overallImpressionMaxWeight.toFixed(2)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {WASHROOM_IMPRESSION_OPTIONS.map(opt => (
                              <button key={opt.value} onClick={() => setWashroomImpression(opt.value)}
                                data-testid={`washroom-impression-${opt.value}`}
                                style={{
                                  flex: 1, padding: '10px 6px', borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                                  fontSize: '12px', fontWeight: '600', transition: 'all 0.2s ease',
                                  border: washroomImpression === opt.value ? '2px solid #D4AF37' : '2px solid #E5E7EB',
                                  background: washroomImpression === opt.value ? 'rgba(212,175,55,0.1)' : '#FFF',
                                  color: washroomImpression === opt.value ? '#92700C' : '#6B7280',
                                }}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Live Badge Preview */}
              <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px' }}>Certification Preview</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: '10px',
                    background: hygieneScore >= 4 ? 'rgba(34,197,94,0.1)' : '#F9FAFB', border: hygieneScore >= 4 ? '1px solid #22C55E' : '1px solid #E5E7EB' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img src="/images/badges/ht-certified-dining.png" alt="" style={{ height: '32px', objectFit: 'contain', opacity: hygieneScore >= 4 ? 1 : 0.3 }} />
                      <span style={{ fontSize: '13px', fontWeight: '600', color: hygieneScore >= 4 ? '#065F46' : '#6B7280' }}>HT Certified Dining</span>
                    </div>
                    <span style={{ fontSize: '12px', color: hygieneScore >= 4 ? '#22C55E' : '#9CA3AF', fontWeight: '600' }}>
                      {hygieneScore.toFixed(1)}/5 {hygieneScore >= 4 ? '' : `(need ${(4 - hygieneScore).toFixed(1)} more)`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: '10px',
                    background: washroomRating >= 4 ? 'rgba(59,130,246,0.1)' : '#F9FAFB', border: washroomRating >= 4 ? '1px solid #3B82F6' : '1px solid #E5E7EB' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img src="/images/badges/ht-certified-washroom.png" alt="" style={{ height: '32px', objectFit: 'contain', opacity: washroomRating >= 4 ? 1 : 0.3 }} />
                      <span style={{ fontSize: '13px', fontWeight: '600', color: washroomRating >= 4 ? '#1E40AF' : '#6B7280' }}>HT Certified Washroom</span>
                    </div>
                    <span style={{ fontSize: '12px', color: washroomRating >= 4 ? '#3B82F6' : '#9CA3AF', fontWeight: '600' }}>
                      {washroomRating.toFixed(1)}/5 {washroomRating >= 4 ? '' : `(need ${(4 - washroomRating).toFixed(1)} more)`}
                    </span>
                  </div>
                  {hygieneScore >= 4 && washroomRating >= 4 && (
                    <div style={{ padding: '12px', borderRadius: '10px', background: 'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05))', border: '1px solid #D4AF37', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                      <img src="/images/badges/ht-certified-dhaba.png" alt="" style={{ height: '40px', objectFit: 'contain' }} />
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#92700C' }}>
                        HT Certified Dhaba — Both criteria met!
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Admin Notes & Actions */}
              <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                {/* Save Draft */}
                <button onClick={saveDraft} data-testid="save-draft-btn"
                  style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '2px solid #E5E7EB',
                    background: '#F9FAFB', color: '#6B7280', fontWeight: '600', fontSize: '13px', cursor: 'pointer', marginBottom: '12px' }}>
                  Save Progress (Draft)
                </button>

                {/* Action buttons — always visible */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: activeAction ? '16px' : '0' }}>
                  <button onClick={() => setActiveAction(activeAction === 'reject' ? null : 'reject')} data-testid="reject-btn"
                    style={{ flex: 1, padding: '12px', borderRadius: '12px', border: activeAction === 'reject' ? '2px solid #EF4444' : '2px solid #E5E7EB',
                      background: activeAction === 'reject' ? '#FEF2F2' : '#FFF', color: activeAction === 'reject' ? '#DC2626' : '#6B7280',
                      fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                    Reject
                  </button>
                  <button onClick={() => setActiveAction(activeAction === 'request_info' ? null : 'request_info')} data-testid="request-info-btn"
                    style={{ flex: 1, padding: '12px', borderRadius: '12px', border: activeAction === 'request_info' ? '2px solid #F59E0B' : '2px solid #E5E7EB',
                      background: activeAction === 'request_info' ? '#FFFBEB' : '#FFF', color: activeAction === 'request_info' ? '#92400E' : '#6B7280',
                      fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                    Request Info
                  </button>
                  <button onClick={() => setActiveAction(activeAction === 'approve' ? null : 'approve')} data-testid="approve-action-btn"
                    style={{ flex: 1, padding: '12px', borderRadius: '12px', border: activeAction === 'approve' ? '2px solid #D4AF37' : '2px solid #E5E7EB',
                      background: activeAction === 'approve' ? 'rgba(212,175,55,0.1)' : '#FFF', color: activeAction === 'approve' ? '#92700C' : '#6B7280',
                      fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                    Approve
                  </button>
                </div>

                {/* Conditional field based on selected action */}
                {activeAction === 'reject' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#DC2626', marginBottom: '6px' }}>Rejection Reason *</label>
                    <textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} rows={3}
                      placeholder="Why is this registration being rejected?"
                      data-testid="rejection-reason-input" autoFocus
                      style={{ width: '100%', padding: '12px', border: '2px solid #FECACA', borderRadius: '10px', fontSize: '14px', resize: 'vertical', outline: 'none', background: '#FEF2F2' }} />
                    <button onClick={() => handleReview('reject')} disabled={submitting || !rejectionReason.trim()} data-testid="confirm-reject-btn"
                      style={{ width: '100%', marginTop: '12px', padding: '14px', borderRadius: '12px', border: 'none',
                        background: rejectionReason.trim() ? '#EF4444' : '#E5E7EB', color: '#FFF', fontWeight: '700', fontSize: '15px',
                        cursor: rejectionReason.trim() ? 'pointer' : 'not-allowed' }}>
                      {submitting ? 'Rejecting...' : 'Confirm Reject'}
                    </button>
                  </div>
                )}

                {activeAction === 'request_info' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#92400E', marginBottom: '6px' }}>Remarks for Owner *</label>
                    <textarea value={infoRequestRemarks} onChange={e => setInfoRequestRemarks(e.target.value)} rows={3}
                      placeholder="What information or corrections do you need from the owner?"
                      data-testid="info-request-input" autoFocus
                      style={{ width: '100%', padding: '12px', border: '2px solid #FDE68A', borderRadius: '10px', fontSize: '14px', resize: 'vertical', outline: 'none', background: '#FFFBEB' }} />
                    <button onClick={() => handleReview('request_info')} disabled={submitting || !infoRequestRemarks.trim()} data-testid="confirm-request-info-btn"
                      style={{ width: '100%', marginTop: '12px', padding: '14px', borderRadius: '12px', border: 'none',
                        background: infoRequestRemarks.trim() ? '#F59E0B' : '#E5E7EB', color: infoRequestRemarks.trim() ? '#1A1A1A' : '#9CA3AF', fontWeight: '700', fontSize: '15px',
                        cursor: infoRequestRemarks.trim() ? 'pointer' : 'not-allowed' }}>
                      {submitting ? 'Sending...' : 'Send Request to Owner'}
                    </button>
                  </div>
                )}

                {activeAction === 'approve' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Admin Notes (optional)</label>
                    <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={2}
                      placeholder="Internal notes (not visible to owner)" autoFocus
                      style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '10px', fontSize: '14px', resize: 'vertical', outline: 'none' }} />
                    <button onClick={() => handleReview('approve')} disabled={submitting || !allApprovalChecked} data-testid="confirm-approve-btn"
                      style={{ width: '100%', marginTop: '12px', padding: '14px', borderRadius: '12px', border: 'none', opacity: allApprovalChecked ? 1 : 0.5,
                        background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '15px',
                        cursor: allApprovalChecked ? 'pointer' : 'not-allowed' }}>
                      {submitting ? 'Approving...' : allApprovalChecked ? 'Confirm Approve' : 'Complete checklist first'}
                    </button>
                  </div>
                )}
              </div>

              {/* Owner's resubmit remarks (if any) */}
              {selected.owner_resubmit_remarks && (
                <div style={{ background: '#EFF6FF', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #BFDBFE' }}>
                  <p style={{ fontWeight: '600', color: '#1E40AF', fontSize: '13px', marginBottom: '4px' }}>Owner's Resubmit Remarks:</p>
                  <p style={{ color: '#1E40AF', fontSize: '14px' }}>{selected.owner_resubmit_remarks}</p>
                </div>
              )}

              {message && (
                <div style={{ padding: '12px', borderRadius: '10px', marginBottom: '16px', textAlign: 'center',
                  background: message.includes('approved') ? '#D1FAE5' : message.includes('rejected') ? '#FEE2E2' : '#FEF3C7',
                  color: message.includes('approved') ? '#065F46' : message.includes('rejected') ? '#991B1B' : '#92400E' }}>
                  {message}
                </div>
              )}
            </>
          )}

          {/* Rejection reason for rejected */}
          {selected.registration_status === 'rejected' && selected.rejection_reason && (
            <div style={{ background: '#FEE2E2', borderRadius: '12px', padding: '16px', border: '1px solid #FECACA' }}>
              <p style={{ fontWeight: '600', color: '#991B1B', fontSize: '13px', marginBottom: '4px' }}>Rejection Reason:</p>
              <p style={{ color: '#991B1B', fontSize: '14px' }}>{selected.rejection_reason}</p>
            </div>
          )}

          {/* Info requested status */}
          {selected.registration_status === 'info_requested' && selected.info_request_remarks && (
            <div style={{ background: '#FFFBEB', borderRadius: '12px', padding: '16px', border: '1px solid #FDE68A', marginBottom: '12px' }}>
              <p style={{ fontWeight: '600', color: '#92400E', fontSize: '13px', marginBottom: '4px' }}>Info Requested:</p>
              <p style={{ color: '#92400E', fontSize: '14px' }}>{selected.info_request_remarks}</p>
            </div>
          )}
          {selected.owner_resubmit_remarks && selected.registration_status !== 'pending' && (
            <div style={{ background: '#EFF6FF', borderRadius: '12px', padding: '16px', border: '1px solid #BFDBFE' }}>
              <p style={{ fontWeight: '600', color: '#1E40AF', fontSize: '13px', marginBottom: '4px' }}>Owner's Remarks:</p>
              <p style={{ color: '#1E40AF', fontSize: '14px' }}>{selected.owner_resubmit_remarks}</p>
            </div>
          )}

          {/* Audit History */}
          {auditHistory.length > 0 ? (
            <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginTop: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px' }}>Audit History ({auditHistory.length})</h3>
              {auditHistory.map(audit => (
                <div key={audit.id} style={{ padding: '12px', borderBottom: '1px solid #F3F4F6', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>By: {audit.auditor_name}</span>
                    <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{new Date(audit.audit_date).toLocaleDateString()}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <ScoreBadge label="Hygiene" score={audit.hygiene_score} max={5} />
                    <ScoreBadge label="Washroom" score={audit.washroom_rating} max={5} />
                  </div>
                  {audit.notes && <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>Notes: {audit.notes}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginTop: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', textAlign: 'center' }}>
              <p style={{ color: '#9CA3AF', fontSize: '13px' }}>No hygiene/washroom audit has been conducted for this dhaba yet.</p>
            </div>
          )}

          {/* Activity Timeline */}
          {activityLog.length > 0 && (
            <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginTop: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '16px' }}>Activity Timeline</h3>
              <div style={{ position: 'relative', paddingLeft: '24px' }}>
                <div style={{ position: 'absolute', left: '7px', top: '8px', bottom: '8px', width: '2px', background: '#E5E7EB' }} />
                {activityLog.map((a, idx) => {
                  const actionConfig = {
                    approved: { color: '#22C55E', bg: '#D1FAE5', label: 'Approved' },
                    rejected: { color: '#EF4444', bg: '#FEE2E2', label: 'Rejected' },
                    info_requested: { color: '#F59E0B', bg: '#FEF3C7', label: 'Info Requested' },
                    resubmitted: { color: '#3B82F6', bg: '#DBEAFE', label: 'Resubmitted by Owner' },
                    submitted: { color: '#6B7280', bg: '#F3F4F6', label: 'Submitted' },
                  }[a.action] || { color: '#6B7280', bg: '#F3F4F6', label: a.action };
                  return (
                    <div key={a.id} style={{ position: 'relative', marginBottom: '16px' }}>
                      <div style={{ position: 'absolute', left: '-20px', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: actionConfig.color, border: '2px solid #FFF', boxShadow: '0 0 0 2px ' + actionConfig.color }} />
                      <div style={{ marginBottom: '2px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: actionConfig.color, padding: '2px 8px', borderRadius: '6px', background: actionConfig.bg }}>{actionConfig.label}</span>
                        <span style={{ fontSize: '11px', color: '#9CA3AF', marginLeft: '8px' }}>
                          {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>by {a.actor_name} ({a.actor_role})</p>
                      {a.remarks && <p style={{ fontSize: '12px', color: '#374151', marginTop: '4px', fontStyle: 'italic' }}>"{a.remarks}"</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Image Viewer Modal */}
          {viewerImages && (
            <ImageViewer images={viewerImages} initialIndex={viewerIndex} onClose={() => setViewerImages(null)} />
          )}
        </div>
      </div>
    );
  }

  // ---- List View ----
  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>
      <Header title="Admin Panel" onBack={onBack} />
      {user?.role === 'app_admin' && (
        <div style={{ padding: '0 16px 8px', display: 'flex', gap: '8px' }}>
          <button onClick={() => { setView('admins'); loadAdmins(); }} data-testid="manage-admins-btn"
            style={{ padding: '10px 16px', borderRadius: '10px', border: '1px solid #D4AF37', background: 'rgba(212,175,55,0.1)', color: '#92700C', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>
            Manage Dhaba Admins
          </button>
          <button onClick={() => setView('settlements')} data-testid="settlements-btn"
            style={{ padding: '10px 16px', borderRadius: '10px', border: '1px solid #22C55E', background: 'rgba(34,197,94,0.1)', color: '#065F46', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>
            Settlements
          </button>
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px', padding: '8px 16px', background: '#FFF', borderBottom: '1px solid #E5E7EB', overflowX: 'auto' }}>
        {['pending', 'info_requested', 'approved', 'rejected'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            style={{ flex: 1, padding: '10px 6px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap',
              background: tab === t ? '#1A1A1A' : '#F3F4F6', color: tab === t ? '#D4AF37' : '#6B7280' }}>
            {t === 'info_requested' ? 'Info Req.' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>
      <div style={{ padding: '16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ width: '40px', height: '40px', border: '3px solid #E5E7EB', borderTopColor: '#D4AF37', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
          </div>
        ) : registrations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            <p style={{ fontSize: '40px', marginBottom: '12px' }}>{tab === 'pending' ? '📋' : tab === 'approved' ? '✅' : '❌'}</p>
            <p>No {tab} registrations</p>
          </div>
        ) : registrations.map(d => (
          <div key={d.id} onClick={() => openReview(d)} data-testid={`reg-card-${d.id}`}
            style={{ background: '#FFF', borderRadius: '14px', padding: '16px', marginBottom: '10px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #E5E7EB' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1A1A1A' }}>{d.name}</h3>
              <StatusBadge status={d.registration_status} />
            </div>
            {d.display_id && <p style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '600', marginBottom: '4px', fontFamily: 'monospace' }}>{d.display_id}</p>}
            <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>{d.highway} · {d.address}</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ fontSize: '12px', color: '#9CA3AF' }}>Owner: {d.owner_name} · {d.owner_phone}</p>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {d.ht_certified_dhaba && <CertBadge type="dhaba" />}
                {d.ht_certified_dining && !d.ht_certified_dhaba && <CertBadge type="dining" />}
                {d.ht_certified_washroom && !d.ht_certified_dhaba && <CertBadge type="washroom" />}
                {d.hygiene_score > 0 && <ScoreBadge label="H" score={d.hygiene_score} max={5} compact />}
              </div>
            </div>
            {d.created_at && <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>Submitted: {new Date(d.created_at).toLocaleDateString()}</p>}
          </div>
        ))}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ---- Shared Components ----

function Header({ title, onBack }) {
  return (
    <div style={{ background: 'linear-gradient(180deg, #1A1A1A, #2D2D2D)', padding: '16px', position: 'sticky', top: 0, zIndex: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={onBack} style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)', color: '#D4AF37', fontSize: '18px', cursor: 'pointer' }}>←</button>
        <h1 style={{ fontSize: '18px', fontWeight: '700', color: '#D4AF37', fontFamily: "'Manrope', sans-serif" }}>{title}</h1>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = { pending: { bg: '#FEF3C7', color: '#92400E', label: 'Pending' }, approved: { bg: '#D1FAE5', color: '#065F46', label: 'Approved' }, rejected: { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' }, info_requested: { bg: '#FFFBEB', color: '#92400E', label: 'Info Requested' } }[status] || { bg: '#F3F4F6', color: '#6B7280', label: status };
  return <span style={{ padding: '4px 10px', borderRadius: '12px', background: s.bg, color: s.color, fontSize: '12px', fontWeight: '600' }}>{s.label}</span>;
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F9FAFB' }}>
      <span style={{ fontSize: '13px', color: '#6B7280' }}>{label}</span>
      <span style={{ fontSize: '13px', color: '#1A1A1A', fontWeight: '500', textAlign: 'right', maxWidth: '60%' }}>{value || '—'}</span>
    </div>
  );
}

function ScoreBadge({ label, score, max, compact }) {
  const s = parseFloat(score || 0).toFixed(1);
  const color = scoreColor(score, max);
  if (compact) {
    return <span style={{ fontSize: '12px', fontWeight: '700', color }}>{label}: {s}/{max}</span>;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ fontSize: '13px', color: '#6B7280' }}>{label}:</span>
      <span style={{ fontSize: '15px', fontWeight: '700', color }}>{s}</span>
      <span style={{ fontSize: '12px', color: '#9CA3AF' }}>/ {max}</span>
    </div>
  );
}

function CertBadge({ type }) {
  const config = {
    dhaba: { src: '/images/badges/ht-certified-dhaba.png', alt: 'HT Certified Dhaba' },
    dining: { src: '/images/badges/ht-certified-dining.png', alt: 'HT Certified Dining' },
    washroom: { src: '/images/badges/ht-certified-washroom.png', alt: 'HT Certified Washroom' },
  }[type];
  if (!config) return null;
  return <img src={config.src} alt={config.alt} style={{ height: '28px', objectFit: 'contain' }} />;
}

function ScoreChecklist({ title, criteria, checks, onToggle, score, maxScore }) {
  const color = scoreColor(score, maxScore);
  const pct = (score / maxScore) * 100;

  return (
    <div style={{ background: '#FFF', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A' }}>{title}</h3>
        <div style={{ textAlign: 'right' }}>
          <span style={{ fontSize: '24px', fontWeight: '800', color }}>{score.toFixed(1)}</span>
          <span style={{ fontSize: '14px', color: '#9CA3AF' }}> / {maxScore}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: '6px', background: '#E5E7EB', borderRadius: '3px', marginBottom: '16px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px', transition: 'width 0.3s ease' }} />
      </div>

      {criteria.map(c => (
        <label key={c.id} data-testid={`check-${c.id}`}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <input type="checkbox" checked={!!checks[c.id]} onChange={() => onToggle(c.id)}
              style={{ width: '20px', height: '20px', accentColor: '#D4AF37' }} />
            <span style={{ fontSize: '14px', color: checks[c.id] ? '#1A1A1A' : '#6B7280' }}>{c.label}</span>
          </div>
          <span style={{ fontSize: '12px', color: checks[c.id] ? color : '#D1D5DB', fontWeight: '600', minWidth: '40px', textAlign: 'right' }}>
            +{c.weight.toFixed(2)}
          </span>
        </label>
      ))}
    </div>
  );
}


function SettlementsView({ onBack }) {
  const [summary, setSummary] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settlingDhaba, setSettlingDhaba] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [message, setMessage] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const [summaryRes, settlementsRes] = await Promise.all([
        fetch(`${API}/api/admin/settlements/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/settlements?status=${statusFilter}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const summaryData = await summaryRes.json();
      const settlementsData = await settlementsRes.json();
      setSummary(summaryData);
      setSettlements(settlementsData.settlements || []);
    } catch (e) {
      console.error('Failed to fetch settlements:', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [statusFilter]);

  const handleSettle = async (dhabaId, dhabaName) => {
    if (!window.confirm(`Mark all pending orders for "${dhabaName}" as settled?`)) return;
    setSettlingDhaba(dhabaId);
    try {
      const token = getToken();
      const res = await fetch(`${API}/api/admin/settlements/settle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ dhaba_id: dhabaId }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage(`Settled ₹${data.total_amount} for ${data.settled_count} orders`);
        fetchData();
      } else {
        setMessage(data.detail || 'Settlement failed');
      }
    } catch (e) {
      setMessage('Settlement failed');
    }
    setSettlingDhaba(null);
    setTimeout(() => setMessage(''), 3000);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>
        <Header title="Settlements" onBack={onBack} />
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid #E5E7EB', borderTopColor: '#D4AF37', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>
      <Header title="Settlements" onBack={onBack} />
      <div style={{ padding: '16px' }}>
        
        {/* Summary Cards */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
            <div style={{ background: '#FEF3C7', borderRadius: '14px', padding: '16px', border: '1px solid #FDE68A' }}>
              <p style={{ fontSize: '12px', color: '#92400E', fontWeight: '600', marginBottom: '4px' }}>Pending Payouts</p>
              <p style={{ fontSize: '22px', fontWeight: '800', color: '#92400E' }}>₹{summary.pending_total.toLocaleString()}</p>
              <p style={{ fontSize: '11px', color: '#B45309', marginTop: '4px' }}>{summary.pending_order_count} orders · {summary.pending_dhabas_count} dhabas</p>
            </div>
            <div style={{ background: '#D1FAE5', borderRadius: '14px', padding: '16px', border: '1px solid #A7F3D0' }}>
              <p style={{ fontSize: '12px', color: '#065F46', fontWeight: '600', marginBottom: '4px' }}>Total Settled</p>
              <p style={{ fontSize: '22px', fontWeight: '800', color: '#065F46' }}>₹{summary.settled_total.toLocaleString()}</p>
              <p style={{ fontSize: '11px', color: '#047857', marginTop: '4px' }}>{summary.settled_order_count} orders</p>
            </div>
          </div>
        )}

        {/* Platform Revenue */}
        {summary && (
          <div style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)', borderRadius: '14px', padding: '16px', marginBottom: '20px' }}>
            <p style={{ fontSize: '12px', color: '#D4AF37', fontWeight: '600', marginBottom: '12px' }}>Revenue Summary (Completed Orders)</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>Item Sales</p>
                <p style={{ fontSize: '16px', fontWeight: '700', color: '#FFFFFF' }}>₹{(summary.total_item_sales || 0).toLocaleString()}</p>
              </div>
              <div>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>GST Collected (5%)</p>
                <p style={{ fontSize: '16px', fontWeight: '700', color: '#FFFFFF' }}>₹{(summary.total_gst_collected || 0).toLocaleString()}</p>
              </div>
              <div>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>Platform Fee (tiered)</p>
                <p style={{ fontSize: '16px', fontWeight: '700', color: '#FFFFFF' }}>₹{summary.total_platform_revenue.toLocaleString()}</p>
              </div>
              <div>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>TCS Collected (1%)</p>
                <p style={{ fontSize: '16px', fontWeight: '700', color: '#FFFFFF' }}>₹{summary.total_tcs_collected.toLocaleString()}</p>
              </div>
            </div>
            
            <div style={{ paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#D4AF37', fontWeight: '600' }}>Total Customer Payments</span>
                <span style={{ fontSize: '18px', fontWeight: '800', color: '#D4AF37' }}>
                  ₹{((summary.total_item_sales || 0) + (summary.total_gst_collected || 0) + summary.total_platform_revenue).toLocaleString()}
                </span>
              </div>
              <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                From {summary.completed_order_count || 0} completed orders
              </p>
            </div>
          </div>
        )}

        {/* Status Filter */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {['pending', 'settled'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s}`}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: 'none',
                background: statusFilter === s ? '#1A1A1A' : '#F3F4F6',
                color: statusFilter === s ? '#D4AF37' : '#6B7280',
                fontWeight: '600',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Message */}
        {message && (
          <div style={{
            padding: '12px',
            borderRadius: '10px',
            marginBottom: '16px',
            background: message.includes('Settled') ? '#D1FAE5' : '#FEE2E2',
            color: message.includes('Settled') ? '#065F46' : '#991B1B',
            fontSize: '14px',
            textAlign: 'center',
          }}>
            {message}
          </div>
        )}

        {/* Settlements List */}
        {settlements.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            <p style={{ fontSize: '40px', marginBottom: '12px' }}>{statusFilter === 'pending' ? '💰' : '✅'}</p>
            <p>No {statusFilter} settlements</p>
          </div>
        ) : (
          settlements.map(s => (
            <div
              key={s.dhaba_id}
              data-testid={`settlement-${s.dhaba_id}`}
              style={{
                background: '#FFFFFF',
                borderRadius: '14px',
                padding: '16px',
                marginBottom: '12px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '2px' }}>{s.dhaba_name}</h3>
                  {s.dhaba_display_id && (
                    <p style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '600', fontFamily: 'monospace' }}>{s.dhaba_display_id}</p>
                  )}
                </div>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '8px',
                  background: statusFilter === 'settled' ? '#D1FAE5' : '#FEF3C7',
                  color: statusFilter === 'settled' ? '#065F46' : '#92400E',
                  fontSize: '11px',
                  fontWeight: '600',
                }}>
                  {s.order_count} orders
                </span>
              </div>

              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
                {s.owner_name} · {s.owner_phone}
              </p>

              {/* Financial Breakdown */}
              <div style={{ background: '#F9FAFB', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>Item Total</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>₹{s.total_item_sales.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>GST (5%)</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>₹{s.total_gst.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>Delivery Partner Fee</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#22C55E' }}>FREE</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>Platform Fee (tiered + 18% GST)</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>₹{s.total_platform_fee.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px dashed #E5E7EB' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#1A1A1A' }}>Customer Paid</span>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#1A1A1A' }}>₹{(s.total_item_sales + s.total_gst + s.total_platform_fee).toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>TCS Deducted (1%)</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#EF4444' }}>-₹{s.total_tcs.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #E5E7EB' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#1A1A1A' }}>Net Payout to Dhaba</span>
                  <span style={{ fontSize: '14px', fontWeight: '800', color: '#D4AF37' }}>₹{s.total_net_payout.toLocaleString()}</span>
                </div>
              </div>

              {/* Settle Button (only for pending) */}
              {statusFilter === 'pending' && (
                <button
                  onClick={() => handleSettle(s.dhaba_id, s.dhaba_name)}
                  disabled={settlingDhaba === s.dhaba_id}
                  data-testid={`settle-btn-${s.dhaba_id}`}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                    color: '#FFFFFF',
                    fontWeight: '700',
                    fontSize: '14px',
                    cursor: settlingDhaba === s.dhaba_id ? 'wait' : 'pointer',
                  }}
                >
                  {settlingDhaba === s.dhaba_id ? 'Settling...' : `Settle ₹${s.total_net_payout.toLocaleString()}`}
                </button>
              )}
            </div>
          ))
        )}

        {/* Refresh */}
        <button
          onClick={fetchData}
          data-testid="refresh-settlements-btn"
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '10px',
            border: '1px solid #E5E7EB',
            background: '#F9FAFB',
            color: '#6B7280',
            fontSize: '13px',
            cursor: 'pointer',
            marginTop: '8px',
          }}
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

export default AdminPanel;
